<script setup lang="ts">
import { buildAnyPronounsList, normaliseKey } from '~/src/buildPronoun.ts';
import { ExampleCategory } from '~/src/classes.ts';
import type { Pronoun, MergedPronounGroup } from '~/src/classes.ts';
import { loadPronounExamples, loadPronounLibrary } from '~/src/data.ts';

definePageMeta({
    translatedPaths: (config) => {
        if (!config.pronouns.enabled || !config.pronouns.any) {
            return [];
        }
        return {
            'pronouns-any': {
                paths: withPronounPrefixes(config, [`/${encodeURIComponent(config.pronouns.any)}`]),
            },
            'pronouns-any-group': {
                paths: withPronounPrefixes(config, [`/${encodeURIComponent(config.pronouns.any)}\\::group`]),
            },
        };
    },
});

const { $translator: translator } = useNuxtApp();
const route = useRoute();
const config = useConfig();
const groupKey = route.params.group as string;

const [pronounLibrary, examples] = await Promise.all([loadPronounLibrary(config), loadPronounExamples()]);
const pronouns = pronounLibrary.pronouns;

let pronounGroups: MergedPronounGroup['groups'] = [];
let short = translator.translate('pronouns.any.short');
if (groupKey) {
    const merged = pronounLibrary.byKey()[normaliseKey(groupKey)];
    pronounGroups = merged?.groups;
    short = merged?.short(translator);
}

const exampleCategories = computed(() => {
    return ExampleCategory.from(examples, config);
});

useSimpleHead({
    title: `${translator.translate('pronouns.intro')}: ${short}`.trim(),
    banner: `api/banner${route.path.replace(/\/$/, '')}.png`,
}, translator);

const comprehensive = useComprehensive();

const pronounsChoice = computed(() => {
    if (!pronounGroups.length) {
        return Object.values(pronouns).filter((p) => !p.hidden);
    }

    let choice: Record<string, Pronoun> = {};
    for (const pronounGroup of pronounGroups) {
        choice = { ...choice, ...pronounGroup.groupPronouns };
    }
    return Object.values(choice).filter((p) => !p.hidden);
});

const anyPronouns = buildAnyPronounsList(config, pronounLibrary);
</script>

<template>
    <Page>
        <NotFound v-if="pronounGroups === undefined" />
        <div v-else>
            <h2
                class="d-flex justify-content-between align-items-start align-items-md-center
                    flex-column flex-md-row gap-2"
            >
                <span>
                    <Icon v="tag" />
                    <T>pronouns.intro</T><T>quotation.colon</T>
                </span>
                <ComprehensiveSwitch v-model="comprehensive" />
            </h2>

            <section>
                <div class="alert alert-primary">
                    <h2 class="text-center mb-0">
                        <strong>
                            <Spelling :text="short" />
                        </strong>
                    </h2>
                </div>
            </section>

            <section>
                <h2 class="h4">
                    <Icon v="file-signature" />
                    <T>pronouns.examples.header</T><T>quotation.colon</T>
                </h2>

                <ul>
                    <template v-for="exampleCategory in exampleCategories">
                        <ExampleCategoryListItem
                            v-if="!exampleCategory.comprehensive || comprehensive"
                            :example-category="exampleCategory"
                            :pronouns-choice="pronounsChoice"
                            link
                        />
                    </template>
                </ul>
            </section>

            <section>
                <div class="alert alert-light">
                    <Icon v="info-circle" />
                    <T>pronouns.any.description</T>
                    <em>(<T>pronouns.any.options</T>)</em>
                </div>
            </section>

            <PronounGroup
                v-for="pronounGroup in pronounGroups"
                :key="pronounGroup.group.name"
                :pronoun-group="pronounGroup.group"
                :pronouns="pronounGroup.groupPronouns"
            />

            <AdPlaceholder :phkey="['content-0', 'content-mobile-0']" />

            <PronounGroup :pronouns="anyPronouns" />

            <section>
                <Share :title="`${$t('pronouns.intro')}: ${$t('pronouns.any.short')}`" />
            </section>

            <Separator icon="info" />
            <section class="mb-0">
                <h2 class="h4">
                    <Icon v="info-circle" />
                    <T>home.whatisit</T><T>quotation.colon</T>
                </h2>
                <T>home.about</T>
                <Homepage align="center" />

                <AdPlaceholder :phkey="['content-1', 'content-mobile-1']" />
            </section>
        </div>
    </Page>
</template>
